import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserPlus, Key, ArrowsClockwise, Check, X, Plus, Trash, ChatCircleDots, Printer, WifiHigh, Ruler, Broom, BellRinging } from "@phosphor-icons/react";

const ROLES = ["super_admin", "registrar", "approver", "printer_operator", "enforcement_officer", "auditor", "read_only"];

export default function Admin() {
    const { t } = useI18n();
    const [users, setUsers] = useState([]);
    const [pending, setPending] = useState([]);
    const [allowlist, setAllowlist] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [keys, setKeys] = useState([]);
    const [regions, setRegions] = useState([]);
    const [newRegion, setNewRegion] = useState({ code: "", name_en: "", name_ar: "", name_fr: "", x: 50, y: 50 });
    const [editing, setEditing] = useState(null); // {code, name_en, name_ar, name_fr, x, y}
    const [newUser, setNewUser] = useState({ email: "", password: "", full_name: "", role: "registrar" });
    const [newAllow, setNewAllow] = useState({ type: "domain", value: "" });
    const [bulkAllow, setBulkAllow] = useState("");
    const [approveRole, setApproveRole] = useState({});

    const load = () => {
        api.get("/users").then((r) => setUsers(r.data.users)).catch(() => {});
        api.get("/users/pending").then((r) => setPending(r.data.users)).catch(() => {});
        api.get("/allowlist").then((r) => setAllowlist(r.data.items)).catch(() => {});
        api.get("/sessions").then((r) => setSessions(r.data.sessions)).catch(() => {});
        api.get("/keys").then((r) => setKeys(r.data.keys)).catch(() => {});
        api.get("/admin/regions").then((r) => setRegions(r.data.regions)).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const createUser = async () => {
        try {
            await api.post("/users", newUser);
            toast.success("User created");
            setNewUser({ email: "", password: "", full_name: "", role: "registrar" });
            load();
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("error"));
        }
    };
    const approve = async (uid) => {
        try {
            await api.post(`/users/${uid}/approve`, { role: approveRole[uid] || "read_only" });
            toast.success("Approved");
            load();
        } catch { toast.error(t("error")); }
    };
    const reject = async (uid) => {
        try { await api.post(`/users/${uid}/reject`); toast.success("Rejected"); load(); }
        catch { toast.error(t("error")); }
    };
    const addAllow = async () => {
        try {
            await api.post("/allowlist", newAllow);
            toast.success("Added to allowlist");
            setNewAllow({ type: "domain", value: "" });
            load();
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("error")); }
    };
    const bulkImport = async () => {
        try {
            const { data } = await api.post("/allowlist/bulk", { text: bulkAllow });
            toast.success(`Added ${data.added.length}, skipped ${data.skipped.length}`);
            setBulkAllow("");
            load();
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("error")); }
    };
    const removeAllow = async (aid) => {
        try { await api.delete(`/allowlist/${aid}`); toast.success("Removed"); load(); }
        catch { toast.error(t("error")); }
    };
    const revokeSession = async (sid) => {
        try { await api.delete(`/sessions/${sid}`); toast.success("Session revoked"); load(); }
        catch { toast.error(t("error")); }
    };
    const rotate = async () => {
        try { await api.post("/keys/rotate"); toast.success("Key rotated"); load(); }
        catch { toast.error(t("error")); }
    };

    const userNameById = (uid) => users.find((u) => u.id === uid)?.email || uid?.slice(0, 8);

    return (
        <div className="space-y-8" data-testid="admin-page">
            <div>
                <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· CONTROL PANEL</div>
                <h1 className="text-3xl font-bold tracking-tight mt-1">{t("admin")}</h1>
            </div>

            <DeploymentRibbon />

            {/* Pending Approvals */}
            <Card className="p-6 rounded-sm border-border">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold">
                        Pending Approvals <span className="ms-2 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 bg-primary text-primary-foreground rounded-sm text-[10px]" data-testid="pending-count">{pending.length}</span>
                    </div>
                </div>
                {pending.length === 0 && <div className="text-sm text-muted-foreground">{t("adminPendingEmpty")}</div>}
                <ul className="divide-y divide-border">
                    {pending.map((u) => (
                        <li key={u.id} className="py-3 flex items-center justify-between gap-4" data-testid={`pending-row-${u.id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                {u.picture ? (
                                    <img src={u.picture} alt="" className="h-10 w-10 rounded-full bg-secondary" />
                                ) : (
                                    <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center text-xs font-bold">{u.email?.[0]?.toUpperCase()}</div>
                                )}
                                <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{u.full_name}</div>
                                    <div className="text-xs mono text-muted-foreground truncate">{u.email}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <select className="h-9 border border-input rounded-md bg-background px-2 text-sm"
                                    value={approveRole[u.id] || "read_only"}
                                    onChange={(e) => setApproveRole({ ...approveRole, [u.id]: e.target.value })}
                                    data-testid={`pending-role-${u.id}`}>
                                    {ROLES.map((r) => <option key={r} value={r}>{t(`role_${r}`)}</option>)}
                                </select>
                                <Button size="sm" className="bg-green-700 hover:bg-green-800 text-white rounded-sm" onClick={() => approve(u.id)} data-testid={`pending-approve-${u.id}`}><Check size={14} weight="bold" className="me-1" />{t("approve")}</Button>
                                <Button size="sm" variant="outline" onClick={() => reject(u.id)} data-testid={`pending-reject-${u.id}`}><X size={14} className="me-1" />{t("adminReject")}</Button>
                            </div>
                        </li>
                    ))}
                </ul>
            </Card>

            {/* Allowlist */}
            <Card className="p-6 rounded-sm border-border">
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3">{t("adminAllowlistTitle")}</div>
                <div className="grid md:grid-cols-4 gap-2 mb-4">
                    <select className="h-10 border border-input rounded-md bg-background px-2 text-sm" value={newAllow.type} onChange={(e) => setNewAllow({ ...newAllow, type: e.target.value })} data-testid="allow-type">
                        <option value="domain">domain</option>
                        <option value="email">email</option>
                    </select>
                    <Input className="md:col-span-2" placeholder={newAllow.type === "domain" ? "utalmea.demo" : "person@example.com"} value={newAllow.value} onChange={(e) => setNewAllow({ ...newAllow, value: e.target.value })} data-testid="allow-value" />
                    <Button onClick={addAllow} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="allow-add"><Plus size={14} weight="bold" className="me-1" />{t("add")}</Button>
                </div>
                <div className="mb-4 border border-dashed border-border rounded-sm p-3 bg-secondary/30">
                    <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-2">Bulk import — paste domains and/or emails (any whitespace, comma, or newline)</div>
                    <textarea
                        value={bulkAllow}
                        onChange={(e) => setBulkAllow(e.target.value)}
                        placeholder="utalmea.demo&#10;chief@utalmea.demo"
                        rows={4}
                        className="w-full mono text-xs border border-input rounded-sm p-2 bg-background"
                        data-testid="allow-bulk-textarea"
                    />
                    <div className="flex justify-end mt-2">
                        <Button size="sm" onClick={bulkImport} disabled={!bulkAllow.trim()} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="allow-bulk-import">{t("adminImport")}</Button>
                    </div>
                </div>
                <table className="w-full data-grid text-sm">
                    <thead className="bg-secondary text-xs uppercase tracking-widest">
                        <tr><th className="text-start">{t("adminType")}</th><th className="text-start">{t("adminValue")}</th><th className="text-start">{t("adminAdded")}</th><th></th></tr>
                    </thead>
                    <tbody>
                        {allowlist.map((a) => (
                            <tr key={a.id} className="border-t border-border" data-testid={`allow-row-${a.id}`}>
                                <td className="uppercase text-xs tracking-widest">{a.type}</td>
                                <td className="mono">{a.value}</td>
                                <td className="mono text-xs text-muted-foreground">{new Date(a.added_at).toLocaleString()} · {a.added_by}</td>
                                <td className="text-end"><Button variant="ghost" size="sm" onClick={() => removeAllow(a.id)} data-testid={`allow-remove-${a.id}`}><Trash size={14} /></Button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            {/* Users */}
            <Card className="p-6 rounded-sm border-border">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold">{t("adminUsersRoles")}</div>
                </div>
                <div className="grid md:grid-cols-5 gap-2 mb-4">
                    <Input placeholder={t("email")} value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email" />
                    <Input placeholder={t("fullName")} value={newUser.full_name} onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })} data-testid="new-user-name" />
                    <Input type="password" placeholder={t("password")} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="new-user-password" />
                    <select className="h-10 border border-input rounded-md bg-background px-2 text-sm" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} data-testid="new-user-role">
                        {ROLES.map((r) => <option key={r} value={r}>{t(`role_${r}`)}</option>)}
                    </select>
                    <Button onClick={createUser} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="new-user-create"><UserPlus size={16} weight="bold" className="me-1" />Create</Button>
                </div>
                <table className="w-full data-grid text-sm">
                    <thead className="bg-secondary text-xs uppercase tracking-widest">
                        <tr><th className="text-start">{t("email")}</th><th className="text-start">{t("adminName")}</th><th className="text-start">{t("region")}</th><th className="text-start">MFA</th></tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.id}`}>
                                <td className="mono">{u.email}</td>
                                <td>{u.full_name}</td>
                                <td className="uppercase text-xs tracking-widest">{t(`role_${u.role}`)}</td>
                                <td>{u.mfa_enabled ? "✓" : "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            {/* All Sessions */}
            <Card className="p-6 rounded-sm border-border">
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3">{t("adminAllSessions")}</div>
                <table className="w-full data-grid text-sm">
                    <thead className="bg-secondary text-xs uppercase tracking-widest">
                        <tr><th className="text-start">{t("adminName")}</th><th className="text-start">{t("adminProvider")}</th><th className="text-start">{t("adminDevice")}</th><th className="text-start">{t("adminIP")}</th><th className="text-start">{t("csvCreated")}</th><th></th></tr>
                    </thead>
                    <tbody>
                        {sessions.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">{t("adminNoSessions")}</td></tr>}
                        {sessions.map((s) => (
                            <tr key={s.id} className="border-t border-border" data-testid={`admin-session-${s.id}`}>
                                <td className="mono text-xs">{userNameById(s.user_id)}</td>
                                <td className="uppercase text-[10px] tracking-widest">{s.provider}</td>
                                <td className="text-xs">{s.browser} · {s.device}</td>
                                <td className="mono text-xs">{s.ip}</td>
                                <td className="mono text-xs">{new Date(s.created_at).toLocaleString()}</td>
                                <td className="text-end"><Button variant="ghost" size="sm" onClick={() => revokeSession(s.id)} data-testid={`admin-session-revoke-${s.id}`}><X size={14} weight="bold" className="me-1" />{t("revoke")}</Button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            {/* Signing keys */}
            <Card className="p-6 rounded-sm border-border">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold">Signing keys (Ed25519)</div>
                    <Button onClick={rotate} variant="outline" data-testid="rotate-key"><ArrowsClockwise size={16} className="me-1" />{t("adminRotateKey")}</Button>
                </div>
                <table className="w-full data-grid text-sm">
                    <thead className="bg-secondary text-xs uppercase tracking-widest">
                        <tr><th className="text-start">{t("adminKidCol")}</th><th className="text-start">{t("adminAlgo")}</th><th className="text-start">{t("csvCreated")}</th><th className="text-start">{t("active")}</th></tr>
                    </thead>
                    <tbody>
                        {keys.map((k) => (
                            <tr key={k.kid} className="border-t border-border">
                                <td className="mono">{k.kid}</td>
                                <td className="mono text-xs">{k.algorithm}</td>
                                <td className="mono text-xs">{new Date(k.created_at).toLocaleString()}</td>
                                <td>{k.active ? <span className="inline-flex items-center gap-1 text-green-700 text-xs uppercase tracking-widest"><Key size={12} weight="fill" />{t("active")}</span> : <span className="text-xs text-muted-foreground uppercase tracking-widest">{t("adminRotated")}</span>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            <TwilioConfigCard />
            <PrinterCard />
            <LabelPresetCard />
            <PushTestCard />
            {/* Regions admin — sits after other cards for now */}
            <Card className="p-6 rounded-sm border-border" data-testid="regions-card">
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3">Regions ({regions.filter(r=>r.active).length} active · {regions.filter(r=>!r.active).length} retired)</div>
                <div className="grid md:grid-cols-6 gap-2 mb-4">
                    <Input placeholder={t("adminCode")} value={newRegion.code} onChange={(e)=>setNewRegion({...newRegion, code: e.target.value.toUpperCase()})} maxLength={4} className="mono" data-testid="region-new-code" />
                    <Input placeholder={`${t("adminName")} (EN)`} value={newRegion.name_en} onChange={(e)=>setNewRegion({...newRegion, name_en: e.target.value})} data-testid="region-new-en" />
                    <Input placeholder="الاسم (AR)" value={newRegion.name_ar} onChange={(e)=>setNewRegion({...newRegion, name_ar: e.target.value})} data-testid="region-new-ar" />
                    <Input placeholder={`${t("adminName")} (FR)`} value={newRegion.name_fr} onChange={(e)=>setNewRegion({...newRegion, name_fr: e.target.value})} data-testid="region-new-fr" />
                    <div className="grid grid-cols-2 gap-1">
                        <Input type="number" min="0" max="100" placeholder="x" value={newRegion.x} onChange={(e)=>setNewRegion({...newRegion, x: parseInt(e.target.value)||0})} className="mono" data-testid="region-new-x" />
                        <Input type="number" min="0" max="100" placeholder="y" value={newRegion.y} onChange={(e)=>setNewRegion({...newRegion, y: parseInt(e.target.value)||0})} className="mono" data-testid="region-new-y" />
                    </div>
                    <Button onClick={async ()=>{
                        try { await api.post("/admin/regions", newRegion); toast.success("Region created"); setNewRegion({code:"",name_en:"",name_ar:"",name_fr:"",x:50,y:50}); load(); }
                        catch(e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("error")); }
                    }} disabled={!newRegion.code || !newRegion.name_en} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="region-add">
                        <Plus size={14} weight="bold" className="me-1" />{t("add")}
                    </Button>
                </div>
                <table className="w-full data-grid text-sm">
                    <thead className="bg-secondary text-xs uppercase tracking-widest">
                        <tr>
                            <th className="text-start">{t("adminCode")}</th>
                            <th className="text-start">{t("adminName")} (EN)</th>
                            <th className="text-start">اسم (AR)</th>
                            <th className="text-start">{t("adminName")} (FR)</th>
                            <th className="text-start mono">x,y</th>
                            <th className="text-start">{t("status")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {regions.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">{t("adminNoRegions")}</td></tr>}
                        {regions.map((r) => {
                            const isEdit = editing?.code === r.code;
                            return (
                                <tr key={r.code} className="border-t border-border" data-testid={`region-row-${r.code}`}>
                                    <td className="mono font-bold">{r.code}</td>
                                    <td>{isEdit ? <Input value={editing.name_en} onChange={(e)=>setEditing({...editing, name_en: e.target.value})} data-testid={`region-edit-en-${r.code}`} /> : r.name_en}</td>
                                    <td dir="rtl">{isEdit ? <Input value={editing.name_ar} onChange={(e)=>setEditing({...editing, name_ar: e.target.value})} data-testid={`region-edit-ar-${r.code}`} /> : r.name_ar}</td>
                                    <td>{isEdit ? <Input value={editing.name_fr} onChange={(e)=>setEditing({...editing, name_fr: e.target.value})} data-testid={`region-edit-fr-${r.code}`} /> : r.name_fr}</td>
                                    <td className="mono text-xs">
                                        {isEdit ? (
                                            <div className="flex gap-1"><Input type="number" min="0" max="100" value={editing.x} onChange={(e)=>setEditing({...editing, x: parseInt(e.target.value)||0})} className="w-14 mono" /><Input type="number" min="0" max="100" value={editing.y} onChange={(e)=>setEditing({...editing, y: parseInt(e.target.value)||0})} className="w-14 mono" /></div>
                                        ) : `${r.x},${r.y}`}
                                    </td>
                                    <td>{r.active ? <span className="inline-flex text-green-700 text-xs uppercase tracking-widest">{t("active")}</span> : <span className="text-xs text-muted-foreground uppercase tracking-widest">{t("adminRetired")}</span>}</td>
                                    <td className="text-end">
                                        {isEdit ? (
                                            <div className="flex gap-1 justify-end">
                                                <Button size="sm" onClick={async ()=>{
                                                    try { await api.put(`/admin/regions/${r.code}`, editing); toast.success("Saved"); setEditing(null); load(); }
                                                    catch(e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("error")); }
                                                }} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid={`region-save-${r.code}`}><Check size={14} weight="bold" /></Button>
                                                <Button size="sm" variant="ghost" onClick={()=>setEditing(null)} data-testid={`region-cancel-${r.code}`}><X size={14} weight="bold" /></Button>
                                            </div>
                                        ) : (
                                            <div className="flex gap-1 justify-end">
                                                {r.active && <Button size="sm" variant="ghost" onClick={()=>setEditing({code: r.code, name_en: r.name_en, name_ar: r.name_ar, name_fr: r.name_fr, x: r.x, y: r.y})} data-testid={`region-edit-${r.code}`}><Ruler size={14} weight="bold" /></Button>}
                                                {r.active && <Button size="sm" variant="ghost" onClick={async ()=>{
                                                    if (!window.confirm(`Retire region ${r.code}?`)) return;
                                                    try { await api.delete(`/admin/regions/${r.code}`); toast.success("Retired"); load(); }
                                                    catch(e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("error")); }
                                                }} data-testid={`region-retire-${r.code}`}><Trash size={14} weight="bold" /></Button>}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </Card>
        </div>
    );
}

function DeploymentRibbon() {
    const [s, setS] = useState(null);
    useEffect(() => { api.get("/admin/deployment-status").then((r) => setS(r.data)).catch(() => {}); }, []);
    if (!s) return null;
    const sovereign = s.hosted_by === "self";
    const authLabel = s.auth_mode === "jwt_only" ? "JWT only" : s.auth_mode === "google_oauth" ? "Google OAuth" : "Google + JWT";
    const signerLabel = s.signing_backend === "hsm" ? "HSM (PKCS#11)" : "Local (PyNaCl)";
    const emailLabel = s.email_provider === "smtp" ? "SMTP" : s.email_provider === "emergent" ? "Emergent" : s.email_provider;
    return (
        <div data-testid="deployment-ribbon" className={`flex flex-wrap items-center gap-3 rounded-sm border px-4 py-2.5 text-xs ${sovereign ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-secondary/30"}`}>
            <div className={`inline-flex items-center gap-1 text-[10px] tracking-[0.3em] uppercase font-bold ${sovereign ? "text-emerald-500" : "text-muted-foreground"}`}>
                {sovereign ? "● Self-hosted" : "● Emergent-managed"}
            </div>
            <div className="h-3 w-px bg-border" />
            <RibbonTag label="Auth" value={authLabel} testid="rib-auth" />
            <RibbonTag label="Signer" value={signerLabel} testid="rib-signer" highlight={s.signing_backend === "hsm"} />
            <RibbonTag label="Email" value={emailLabel} testid="rib-email" />
        </div>
    );
}

function RibbonTag({ label, value, testid, highlight }) {
    return (
        <div className="inline-flex items-center gap-1.5" data-testid={testid}>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
            <span className={`mono text-xs ${highlight ? "text-emerald-500 font-bold" : ""}`}>{value}</span>
        </div>
    );
}

function PushTestCard() {
    const [busy, setBusy] = useState(false);
    const [lastAt, setLastAt] = useState(null);
    const send = async () => {
        setBusy(true);
        try {
            await api.post("/push/test");
            setLastAt(new Date().toLocaleString());
            toast.success("Test push queued to all subscribed devices");
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Push test failed");
        }
        setBusy(false);
    };
    return (
        <Card className="p-6 rounded-sm border-border" data-testid="push-test-card">
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3 flex items-center gap-2">
                <BellRinging size={14} weight="duotone" /> Push notifications · smoke test
            </div>
            <p className="text-xs text-muted-foreground mb-4">
                Broadcasts a "test" push to every subscribed field-officer device.
                Subscribers must opt in to the <span className="mono">test</span> category to receive it —
                that keeps operational alerts uncluttered.
            </p>
            <div className="flex items-center gap-3">
                <Button onClick={send} disabled={busy} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="push-test-send">
                    <BellRinging size={14} weight="fill" className="me-1" />Send test push
                </Button>
                {lastAt && (
                    <span className="text-xs mono text-muted-foreground" data-testid="push-test-lastat">last sent {lastAt}</span>
                )}
            </div>
        </Card>
    );
}

function PrinterCard() {
    const { t } = useI18n();
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({ host: "", port: 9100 });
    const [busy, setBusy] = useState(false);
    const [ping, setPing] = useState(null);
    const load = () => api.get("/admin/printer").then((r) => setCfg(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);
    const save = async () => {
        setBusy(true);
        try {
            await api.patch("/admin/printer", form);
            toast.success("Printer saved");
            setForm({ host: "", port: 9100 });
            load();
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed"); }
        setBusy(false);
    };
    const doPing = async () => {
        setBusy(true); setPing(null);
        try {
            const { data } = await api.post("/admin/printer/ping");
            setPing(data);
            if (data.status === "ok") toast.success(`Printer reachable · ${data.latency_ms}ms`);
            else toast.error(`Printer ${data.status}${data.error ? ": " + data.error : ""}`);
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Ping failed"); }
        setBusy(false);
    };
    const clearCfg = async () => {
        if (!window.confirm("Clear the printer configuration? Future ZPL LAN sends will error until a new host/port is saved.")) return;
        setBusy(true);
        try {
            await api.delete("/admin/printer");
            toast.success("Printer config cleared");
            setPing(null);
            load();
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Clear failed"); }
        setBusy(false);
    };
    return (
        <Card className="p-6 rounded-sm border-border" data-testid="printer-card">
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3 flex items-center gap-2">
                <Printer size={14} weight="duotone" /> Zebra Printer · LAN
            </div>
            <div className="mb-3 text-sm">
                Status:{" "}
                {cfg?.configured ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-green-100 text-green-900 border border-green-400 text-xs uppercase tracking-widest" data-testid="printer-status-configured">
                        CONFIGURED · {cfg.printer?.host}:{cfg.printer?.port}
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-amber-100 text-amber-900 border border-amber-400 text-xs uppercase tracking-widest" data-testid="printer-status-none">{t("adminNotSet")}</span>
                )}
                {cfg?.printer?.updated_at && (
                    <span className="ms-3 mono text-xs text-muted-foreground">updated {new Date(cfg.printer.updated_at).toLocaleString()}</span>
                )}
            </div>
            <div className="grid md:grid-cols-3 gap-2 mb-3">
                <Input placeholder={`${t("adminHost")} (10.0.0.20)`} value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} data-testid="printer-host" />
                <Input type="number" placeholder={`${t("adminPort")} (9100)`} value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} data-testid="printer-port" />
                <div className="flex gap-2">
                    <Button onClick={save} disabled={busy || !form.host} className="bg-primary hover:bg-primary/90 rounded-sm flex-1" data-testid="printer-save">{t("save")}</Button>
                </div>
            </div>
            {cfg?.configured && (
                <div className="flex items-center gap-3 flex-wrap">
                    <Button variant="outline" onClick={doPing} disabled={busy} data-testid="printer-ping-btn"><WifiHigh size={14} weight="bold" className="me-1" />{t("adminPing")}</Button>
                    <Button variant="ghost" onClick={clearCfg} disabled={busy} className="text-primary hover:text-primary hover:bg-primary/10" data-testid="printer-clear-btn"><Broom size={14} weight="bold" className="me-1" />{t("adminClearConfig")}</Button>
                    {ping && (
                        <div className="text-xs mono" data-testid="printer-ping-result">
                            {ping.status === "ok" ? (
                                <span className="text-green-700">✓ ok · {ping.latency_ms}ms</span>
                            ) : ping.status === "timeout" ? (
                                <span className="text-amber-700">⚠ timeout</span>
                            ) : ping.status === "refused" ? (
                                <span className="text-primary">✕ refused</span>
                            ) : (
                                <span className="text-primary">✕ {ping.status}{ping.error ? " · " + ping.error : ""}</span>
                            )}
                        </div>
                    )}
                </div>
            )}
            <div className="text-xs text-muted-foreground mt-3">
                Raw ZPL is streamed over TCP to the Zebra port (typically 9100). Timeout 3s. Every send is audited.
            </div>
        </Card>
    );
}

function LabelPresetCard() {
    const { t } = useI18n();
    const [presets, setPresets] = useState([]);
    const [form, setForm] = useState({ key: "", label: "", w_mm: 100, h_mm: 50 });
    const [busy, setBusy] = useState(false);
    const load = () => api.get("/plates/label-presets").then((r) => setPresets(r.data.presets || [])).catch(() => {});
    useEffect(() => { load(); }, []);
    const isCustom = (p) => !["eu-sticker", "sticker-lg", "sticker-sm", "sticker-sq"].includes(p.key);
    const add = async () => {
        setBusy(true);
        try {
            await api.post("/admin/label-presets", form);
            toast.success("Preset added");
            setForm({ key: "", label: "", w_mm: 100, h_mm: 50 });
            load();
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed"); }
        setBusy(false);
    };
    const del = async (key) => {
        try {
            await api.delete(`/admin/label-presets/${key}`);
            toast.success("Removed"); load();
        } catch { toast.error("Failed"); }
    };
    return (
        <Card className="p-6 rounded-sm border-border" data-testid="preset-card">
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3 flex items-center gap-2">
                <Ruler size={14} weight="duotone" /> Custom Label Presets
            </div>
            <div className="grid md:grid-cols-5 gap-2 mb-4">
                <Input placeholder="key (kebab-case)" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase() })} data-testid="preset-key" />
                <Input placeholder={t("adminLabel")} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="preset-label" />
                <Input type="number" placeholder="w mm" value={form.w_mm} onChange={(e) => setForm({ ...form, w_mm: Number(e.target.value) })} data-testid="preset-w" />
                <Input type="number" placeholder="h mm" value={form.h_mm} onChange={(e) => setForm({ ...form, h_mm: Number(e.target.value) })} data-testid="preset-h" />
                <Button onClick={add} disabled={busy || !form.key || !form.label} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="preset-add"><Plus size={14} weight="bold" className="me-1" />{t("add")}</Button>
            </div>
            <table className="w-full data-grid text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-widest">
                    <tr><th className="text-start">Key</th><th className="text-start">{t("adminLabel")}</th><th className="text-start">{t("adminSizeMm")}</th><th></th></tr>
                </thead>
                <tbody>
                    {presets.map((p) => (
                        <tr key={p.key} className="border-t border-border" data-testid={`preset-row-${p.key}`}>
                            <td className="mono">{p.key}{!isCustom(p) && <span className="ms-2 text-[10px] tracking-widest text-muted-foreground">{t("adminBuiltIn")}</span>}</td>
                            <td>{p.label}</td>
                            <td className="mono">{p.w_mm} × {p.h_mm}</td>
                            <td className="text-end">
                                {isCustom(p) && (
                                    <Button variant="ghost" size="sm" onClick={() => del(p.key)} data-testid={`preset-del-${p.key}`}><Trash size={14} /></Button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Card>
    );
}

function TwilioConfigCard() {
    const [cfg, setCfg] = useState(null);
    const [balance, setBalance] = useState(null);
    const [form, setForm] = useState({ account_sid: "", auth_token: "", from_number: "" });
    const [testTo, setTestTo] = useState("");
    const [busy, setBusy] = useState(false);
    const load = () => {
        api.get("/admin/twilio").then((r) => setCfg(r.data));
        api.get("/admin/twilio/balance").then((r) => setBalance(r.data)).catch(() => {});
    };
    useEffect(() => { load(); }, []);
    const save = async () => {
        setBusy(true);
        try {
            await api.patch("/admin/twilio", form);
            toast.success("Twilio config saved");
            setForm({ account_sid: "", auth_token: "", from_number: "" });
            load();
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed"); }
        setBusy(false);
    };
    const clear = async () => { await api.delete("/admin/twilio"); toast.success("Cleared"); load(); };
    const test = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/twilio/test", { to: testTo });
            toast.success(`Test SMS sent (${data.sid})`);
            load();
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Test failed"); }
        setBusy(false);
    };
    return (
        <Card className="p-6 rounded-sm border-border" data-testid="twilio-card">
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3 flex items-center gap-2">
                <ChatCircleDots size={14} weight="duotone" /> Twilio SMS · Live Switch
            </div>
            <div className="mb-4 text-sm">
                Status:{" "}
                {cfg?.configured ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-green-100 text-green-900 border border-green-400 text-xs uppercase tracking-widest" data-testid="twilio-status-live">LIVE {cfg.source === "env" ? "· ENV" : "· DB"}</span>
                ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-amber-100 text-amber-900 border border-amber-400 text-xs uppercase tracking-widest" data-testid="twilio-status-mocked">MOCKED</span>
                )}
                {cfg?.configured && (
                    <span className="ms-3 mono text-xs text-muted-foreground">{cfg.account_sid} · {cfg.from_number} · token {cfg.auth_token_masked}</span>
                )}
                {cfg?.verified_at && <span className="ms-3 text-xs text-green-700">verified {new Date(cfg.verified_at).toLocaleString()}</span>}
            </div>
            {cfg?.configured && balance && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4 text-sm" data-testid="twilio-balance-card">
                    <div className="border border-border rounded-sm p-3">
                        <div className="text-[10px] tracking-widest uppercase text-muted-foreground">Balance</div>
                        <div className="mono mt-1 text-2xl font-bold" data-testid="twilio-balance">
                            {balance.balance ? `${balance.balance} ${balance.currency || ""}` : (balance.error ? "—" : "…")}
                        </div>
                    </div>
                    <div className="border border-border rounded-sm p-3">
                        <div className="text-[10px] tracking-widest uppercase text-muted-foreground">Last delivery</div>
                        <div className="mono mt-1 text-xs" data-testid="twilio-last-delivery">
                            {balance.last_delivery ? `${balance.last_delivery.action} · ${new Date(balance.last_delivery.ts).toLocaleString()}` : "none yet"}
                        </div>
                        {balance.last_delivery?.meta?.to && <div className="text-[10px] mono text-muted-foreground truncate">→ {balance.last_delivery.meta.to}</div>}
                    </div>
                    {balance.error && (
                        <div className="border border-primary/40 rounded-sm p-3 bg-primary/5 text-xs text-primary">
                            Balance fetch failed: {String(balance.error).slice(0, 100)}
                        </div>
                    )}
                </div>
            )}
            <div className="grid md:grid-cols-3 gap-2 mb-3">
                <Input placeholder="Account SID (AC…)" value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} data-testid="twilio-sid" />
                <Input placeholder="Auth token" type="password" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} data-testid="twilio-token" />
                <Input placeholder="From (+E164)" value={form.from_number} onChange={(e) => setForm({ ...form, from_number: e.target.value })} data-testid="twilio-from" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <Button onClick={save} disabled={busy || !form.account_sid || !form.auth_token || !form.from_number} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="twilio-save">Save credentials</Button>
                {cfg?.configured && (
                    <>
                        <Input placeholder="+963991112222" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="max-w-xs" data-testid="twilio-test-to" />
                        <Button variant="outline" onClick={test} disabled={busy || !testTo} data-testid="twilio-test-btn">Send test SMS</Button>
                        <Button variant="ghost" onClick={clear} disabled={busy} data-testid="twilio-clear">Clear</Button>
                    </>
                )}
            </div>
            <div className="text-xs text-muted-foreground mt-3">
                Saved credentials override <span className="mono">/app/backend/.env</span>. A successful test SMS flips the renewal cron from MOCKED to LIVE on the very next fire.
            </div>
        </Card>
    );
}
